import { MoreMenuIcon } from "@taunggyi-plus/design-system";
import { AdminBackupPage } from "./backup-page";
import { AdminOrders } from "./admin-orders";
import {
  AdminAnnouncements,
  AdminDirectory,
  TaxonomySettings,
  ContentSettings,
  ManagedRiders,
  AdminTeam,
  AdPackages,
  EventsWorkspace,
  ProviderSettings,
} from "@taunggyi-plus/finance-feature";
import { WorkThresholds } from "./work-thresholds";
import { AdminTaxiDrivers } from "./taxi-drivers";
import { DashboardMetrics } from "./dashboard-metrics";
import { DashboardQueue } from "./dashboard-queue";
import { BrandLogo, Card } from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";
import { RoleProfilePage } from "@taunggyi-plus/customer-feature";
import {
  AdminAuditTrail,
  AdminPromotionReview,
  AdminSubscriptionManagement,
  FinanceOverview,
  TaxiSubscriptionRequests,
} from "@taunggyi-plus/finance-feature";
import { AdminPartnerApprovals, AdminPublicationControls } from "@taunggyi-plus/onboarding-feature";

export function AdminDashboard() {
  return (
    <div className="role-dashboard role-dashboard--admin">
      <DashboardMetrics />
    </div>
  );
}

export function AdminOrdersPage() {
  return (
    <div className="role-dashboard role-dashboard--admin-orders">
      <AdminOrders />
    </div>
  );
}

export function AdminFinancePage() {
  return (
    <div className="role-dashboard role-dashboard--admin-finance">
      <FinanceOverview role="admin" />
    </div>
  );
}

const adminMenu = [
  ["/more/service-help", "Order support & refunds", "Resolve customer cases"],
  ["/more/custom-notifications", "Custom notifications", "Compose notifications for account roles"],
  ["/more/customers", "Customers", "Customer accounts"],
  ["/more/categories", "Categories & tags", "Manage shared catalog choices"],
  ["/more/how-to-use", "How to use Link", "App guide"],
  ["/more/profile", "Profile", "Administrator identity and access"],
  ["/more/work-thresholds", "Overdue thresholds", "Set waiting-time limits for each service"],
  ["/more/approvals", "Partner approvals", "Review applications across all services"],
  ["/more/subscription-requests", "Subscription requests", "Review provider subscription payments"],
  ["/more/promotions", "Promotions", "Review partner promotion requests"],
  ["/more/subscriptions", "Subscriptions", "Manage plans and renewal dates"],
  ["/more/audit", "Audit", "Review sensitive platform actions"],
  ["/more/team", "Admin team", "Manage finance, support and management permissions"],
  ["/more/ad-packages", "Advertising packages", "Set placements, fees and view campaign results"],
  ["/more/events", "Events", "Manage events, participants and fees"],
  [
    "/more/provider-settings",
    "Provider availability",
    "Manage opening hours and subscription grace periods",
  ],
  ["/more/services", "Services", "Manage publication and subscriptions"],
  ["/more/taxi-drivers", "Taxi Driver List", "Manage taxi driver information and verification"],
  ["/more/hotels", "Hotel List", "Review hotel partners"],
  ["/more/merchants", "Merchant List", "Review merchant partners"],
  ["/more/delivery-owners", "Delivery Owner List", "Review delivery owner partners"],
  ["/more/delivery-riders", "Delivery Rider List", "Review delivery riders and fleet information"],
  ["/more/backup", "Backup Data", "Review protected backup operations"],
] as const;

export function AdminMore({ locale }: { readonly locale: Locale }) {
  const page = window.location.pathname.replace(/\/+$/, "");
  const params = new URLSearchParams(window.location.search);
  const service = params.get("service") ?? "all";
  const queue = params.get("queue") ?? "pending";
  if (page === "/more/custom-notifications") return <AdminAnnouncements />;
  if (page === "/more/customers") return <AdminDirectory customers />;
  if (page === "/more/categories") return <TaxonomySettings />;
  if (["support", "how-to-use", "legal", "safety"].includes(page.split("/").pop()!))
    return <ContentSettings section={page.split("/").pop()!} />;
  if (page === "/more/team") return <AdminTeam />;
  if (page === "/more/ad-packages") return <AdPackages admin />;
  if (page === "/more/provider-settings") return <ProviderSettings admin />;
  if (page === "/more/work-thresholds") return <WorkThresholds />;
  if (page === "/more/operations")
    return <DashboardQueue key={`${service}:${queue}`} service={service} queue={queue} />;
  if (page === "/more/approvals") {
    const kind =
      service === "hotel" ||
      service === "merchant" ||
      service === "delivery_fleet" ||
      service === "taxi_group"
        ? service
        : "all";
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <AdminPartnerApprovals key={kind} locale={locale} initialKind={kind} />
      </div>
    );
  }
  if (
    page === "/more/subscription-requests" ||
    page === "/more/promotions" ||
    page === "/more/subscriptions"
  )
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        {page === "/more/subscription-requests" ? (
          <TaxiSubscriptionRequests admin />
        ) : page === "/more/promotions" ? (
          <AdminPromotionReview />
        ) : (
          <AdminSubscriptionManagement showHeading={false} />
        )}
      </div>
    );
  if (page === "/more/delivery-riders") return <ManagedRiders admin />;
  if (page === "/more/profile") return <RoleProfilePage roleTitle="Platform administrator" />;
  if (page === "/more/audit")
    return (
      <>
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <AdminAuditTrail />
      </>
    );
  if (page === "/more/events" || page.startsWith("/more/events/"))
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <EventsWorkspace mode="admin" />
      </div>
    );
  if (page === "/more/services")
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <AdminPublicationControls />
        <AdminSubscriptionManagement />
      </div>
    );
  if (page === "/more/taxi-drivers" || page === "/more/taxi-leaders") return <AdminTaxiDrivers />;
  if (page === "/more/hotels")
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <AdminPartnerApprovals locale={locale} initialKind="hotel" initialStatus="all" />
      </div>
    );
  if (page === "/more/merchants")
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <AdminPartnerApprovals locale={locale} initialKind="merchant" initialStatus="all" />
      </div>
    );
  if (page === "/more/delivery-owners")
    return (
      <div className="role-more-page">
        <a className="more-back-link" href="/more">
          ← More
        </a>
        <AdminPartnerApprovals locale={locale} initialKind="delivery_fleet" initialStatus="all" />
      </div>
    );
  if (page === "/more/backup") return <AdminBackupPage />;

  return (
    <div className="customer-more role-more-menu">
      <Card className="more-profile-card">
        <BrandLogo compact />
        <div>
          <strong>Administration</strong>
        </div>
      </Card>
      <section className="more-group">
        <div className="more-menu-list">
          {adminMenu.map(([href, title]) => (
            <a className="more-menu-row" href={href} key={href}>
              <MoreMenuIcon name={href} />
              <span>
                <strong>{title}</strong>
              </span>
              <b aria-hidden="true">›</b>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
